import { defineComponent as _defineComponent } from 'vue'
import { SportType } from '@/@types/graphql-types';
import { COUNTRY_WITHOUT_TITLEDETAIL } from '@/constants/web-locales.constant';
import { SportCategory } from '@/enums/sports';
import { useQuery } from '@/helpers/composables/useApollo';
import { useI18n } from '@/helpers/composables/useI18n';
import { useLanguageStore } from '@/helpers/composables/useStores';
import { shiftDateWithTimezone, shiftHours } from '@/helpers/date-helper';
import { captureMessageForSentry } from '@/helpers/sentry-helper';
import {
	GetSportsFooterEventsV2Document,
	GetSportsFooterEventsV2Query,
	GetSportsFooterEventsV2QueryVariables,
} from '@/pages/graphql/queries/GetSportsFooterV2.query';
import {
	GetTopCompetitionsDocument,
	GetTopCompetitionsQuery,
	GetTopCompetitionsQueryVariables,
} from '@/pages/graphql/queries/GetTopCompetitions.query';
import { getOverviewUrlBySport } from '@/routing/sports';
import { computed } from 'vue';

type SportsFooterEventsData = NonNullable<GetSportsFooterEventsV2Query['sportEventsV2']['edges']>;


export default /*#__PURE__*/_defineComponent({
  __name: 'SportsDefaultFooter',
  setup(__props) {

const { country, language, localeObject, webLocale } = useLanguageStore();
const { t, tc } = useI18n();
const TOP_SPORTS = [
	SportType.Football,
	SportCategory.Motorsport,
	SportType.Tennis,
	SportType.AmericanFootball,
	SportType.Baseball,
] as const;

const baseQueryVariables = computed<GetSportsFooterEventsV2QueryVariables>(() => {
	// we filter out events that don't have a `getFullPath`.
	// shortcut: it'd be cleaner if the backend added a hasFullPath filter parameter, but we gotta move fast :sonic:.
	// plus, 1) the request itself only increases by 1kb (gzipped) and 2) it's only happening in sports, not on title detail pages.
	const first = 50;
	return { language: language.value, country: country.value, first, timezoneOffset: 0 };
});

const liveDataQueryVariables = computed<GetSportsFooterEventsV2QueryVariables>(() => {
	const date = new Date();
	date.setUTCMinutes(0, 0, 0);

	return {
		...baseQueryVariables.value,
		filter: { startAt: { from: shiftHours(date, -2).toISOString() } },
	};
});

const liveDataQuery = useQuery<GetSportsFooterEventsV2Query, GetSportsFooterEventsV2QueryVariables>(
	GetSportsFooterEventsV2Document,
	liveDataQueryVariables,
	{
		errorPolicy: 'all',
	}
);

const liveData = computed(() => {
	return liveDataQuery.result.value?.sportEventsV2.edges ?? [];
});

liveDataQuery.onError(error => {
	captureMessageForSentry(
		'[GraphQL Get Current Sport Events (partial)]:',
		{ error: error, where: 'Component: SportsDefaultFooter' },
		'error'
	);
});

const upcomingDataQueryVariables = computed<GetSportsFooterEventsV2QueryVariables>(() => {
	return {
		...baseQueryVariables.value,
		filter: { startAt: { from: shiftDateWithTimezone(localeObject.value.timezone, 1).toISOString() } },
	};
});
const upcomingDataQuery = useQuery<GetSportsFooterEventsV2Query, GetSportsFooterEventsV2QueryVariables>(
	GetSportsFooterEventsV2Document,
	upcomingDataQueryVariables,
	{
		errorPolicy: 'all',
	}
);
upcomingDataQuery.onError(error => {
	captureMessageForSentry(
		'[GraphQL Get Upcoming Sport Events (partial)]:',
		{ error: error, where: 'Component: SportsDefaultFooter' },
		'error'
	);
});
const upcomingData = computed(() => {
	return upcomingDataQuery.result.value?.sportEventsV2.edges ?? [];
});

const topCompetitionsQueryVariables = computed<GetTopCompetitionsQueryVariables>(() => {
	const today = new Date();
	today.setUTCHours(0, 0, 0, 0);

	return {
		country: country.value,
		language: language.value,
		first: 5,
		after: '',
		filter: {
			activeOn: today.toISOString(),
		},
	};
});

const topCompetitionsDataQuery = useQuery<GetTopCompetitionsQuery, GetTopCompetitionsQueryVariables>(
	GetTopCompetitionsDocument,
	topCompetitionsQueryVariables,
	{
		errorPolicy: 'all',
	}
);

topCompetitionsDataQuery.onError(error => {
	captureMessageForSentry(
		'[GraphQL Get Top Competitions (partial)]:',
		{ error: error, where: 'Component: SportsDefaultFooter' },
		'error'
	);
});

const topCompetitionsData = computed(() => {
	return topCompetitionsDataQuery.result.value?.sportCompetitionsV2.edges ?? [];
});
const currentYear = new Date().getFullYear();

const version = process.env.VERSION;

const liveEvents = computed(() => {
	return liveData.value
		.map(event => ({
			text: `${getEventname(event)}: ${event?.node?.content?.name}`,
			path: getFullpath(event),
		}))
		.filter(event => event.path !== '')
		.slice(0, 5);
});

const upcomingEvents = computed(() => {
	return upcomingData.value
		.map(event => ({
			text: `${getEventname(event)}: ${event?.node?.content?.name}`,
			path: getFullpath(event),
		}))
		.filter(event => event.path !== '')
		.slice(0, 5);
});

const topCompetitions = computed(() => {
	return topCompetitionsData.value
		.map(competition => ({
			text: competition?.node?.content?.name,
			path: competition?.node?.content?.fullPath,
		}))
		.filter(event => event.path !== '');
});

const topSports = computed(() => {
	return TOP_SPORTS.map(sport => ({
		text: tc(`SPORTS_WEBAPP_SPORT_TYPE_${sport}`),
		path: getOverviewUrlBySport(sport, webLocale.value),
	})).filter(event => event.path !== '');
});

const footerSections = computed(() => {
	return [
		{
			title: t('SPORTS_WEBAPP_TOP_X_LIVE_EVENTS', { x: liveEvents.value.length }),
			links: liveEvents.value,
		},
		{
			title: t('SPORTS_WEBAPP_TOP_X_UPCOMING_EVENTS', { x: upcomingEvents.value.length }),
			links: upcomingEvents.value,
		},
		{
			title: t('SPORTS_WEBAPP_TOP_X_COMPETITIONS', { x: topCompetitions.value.length }),
			links: topCompetitions.value,
		},
		{
			title: t('SPORTS_WEBAPP_TOP_X_SPORTS', { x: topSports.value.length }),
			links: topSports.value,
		},
	];
});

const titleUrlsEnabled = computed(() => {
	return !COUNTRY_WITHOUT_TITLEDETAIL.includes(webLocale.value);
});

function getFullpath({ node }: SportsFooterEventsData[number]): string {
	if (node.__typename === 'MultiStepSportEvent') {
		return node.parent?.content?.fullPath ?? '';
	}

	return node.content?.fullPath ?? '';
}

function getEventname(event: SportsFooterEventsData[number]): string {
	if (event?.node?.__typename === 'MultiStepSportEvent') {
		return event?.node?.parent?.content?.name ?? '';
	}

	return event?.node?.competition?.content?.name ?? '';
}

return { __sfc: true,country, language, localeObject, webLocale, t, tc, TOP_SPORTS, baseQueryVariables, liveDataQueryVariables, liveDataQuery, liveData, upcomingDataQueryVariables, upcomingDataQuery, upcomingData, topCompetitionsQueryVariables, topCompetitionsDataQuery, topCompetitionsData, currentYear, version, liveEvents, upcomingEvents, topCompetitions, topSports, footerSections, titleUrlsEnabled, getFullpath, getEventname }
}

})